import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { DataTable } from '@/shared/components/data-table'
import QuoteDeleteConfirmDialog from '@/shared/components/QuoteDeleteConfirmDialog'
import { downloadCsv, rowsToCsv } from '@/shared/lib/exportFormat'
import { toast } from '@/shared/lib/toast'

import { deleteQuotation, getQuotationStats } from '../api/quotation.api'
import {
  QuoteColumns,
  QuoteDetailsModal,
  QuoteFilters,
  QuoteHeader,
  QuoteMobileCard,
  QuoteSummaryCards,
  SendQuotationModal,
} from '../components/quotations'
import { useQuotation } from '../hooks/useQuotation'

export default function SalesQuotationsPage() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [searchParams, setSearchParams] = useSearchParams()
  const periodParam = searchParams.get('period') || 'all'
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''

  const [period, setPeriod] = useState(periodParam)

  // Dates selected by the user
  const [startDate, setStartDate] = useState(startDateParam)
  const [endDate, setEndDate] = useState(endDateParam)

  // Dates actually applied to the backend
  const [appliedStartDate, setAppliedStartDate] = useState(startDateParam)
  const [appliedEndDate, setAppliedEndDate] = useState(endDateParam)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    if (startDateParam) {
      setStartDate(startDateParam)
      setAppliedStartDate(startDateParam)
    }
    if (endDateParam) {
      setEndDate(endDateParam)
      setAppliedEndDate(endDateParam)
    }
  }, [periodParam, startDateParam, endDateParam])

  const [quotationStats, setQuotationStats] = useState(null)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  const { quotations, meta, getQuotations, getQuotationById } = useQuotation()

  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedQuote, setSelectedQuote] = useState(null)
  const [isSendOpen, setIsSendOpen] = useState(false)

  // Delete state
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleteQuote, setDeleteQuote] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  /*
   * Debounce search
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [search])

  /*
   * Build API parameters
   */
  const quotationParams = useMemo(
    () => ({
      page,
      limit: 5,
      period,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(period === 'custom'
        ? {
            startDate: appliedStartDate,
            endDate: appliedEndDate,
          }
        : {}),
    }),
    [page, period, debouncedSearch, appliedStartDate, appliedEndDate],
  )

  /*
   * Fetch quotations and quotation stats
   */
  useEffect(() => {
    if (period === 'custom' && (!appliedStartDate || !appliedEndDate)) {
      return
    }

    const loadQuotations = async () => {
      try {
        setLoading(true)

        const [, statsRes] = await Promise.all([
          getQuotations(quotationParams),
          getQuotationStats(quotationParams),
        ])

        setQuotationStats(statsRes?.data || null)
      } catch {
        setQuotationStats(null)
      } finally {
        setLoading(false)
      }
    }

    loadQuotations()
  }, [period, appliedStartDate, appliedEndDate, getQuotations, quotationParams])

  useEffect(() => {
    const state = location.state

    if (!state?.fromQuotationModal || !state?.quotationId) {
      return
    }

    const reopenQuotation = async () => {
      try {
        const quotation = await getQuotationById(state.quotationId)

        setSelectedQuote(quotation)
        setIsDetailsOpen(true)

        navigate(location.pathname, {
          replace: true,
          state: null,
        })
      } catch (error) {
        console.error('Failed to reopen quotation:', error)
      }
    }

    reopenQuotation()
  }, [location, getQuotationById, navigate])

  /*
   * Handle period change
   */
  const handlePeriodChange = (value) => {
    setPeriod(value)

    const updated = new URLSearchParams(searchParams)
    if (value === 'all') {
      updated.delete('period')
      updated.delete('startDate')
      updated.delete('endDate')
    } else {
      updated.set('period', value)
      if (value !== 'custom') {
        updated.delete('startDate')
        updated.delete('endDate')
      }
    }
    setSearchParams(updated)

    // Clear custom dates when switching
    // to a predefined period.
    if (value !== 'custom') {
      setStartDate('')
      setEndDate('')
      setAppliedStartDate('')
      setAppliedEndDate('')
    }
  }

  /*
   * Apply custom date range
   */
  const handleApplyCustomDate = () => {
    if (!startDate || !endDate) {
      return
    }

    if (startDate > endDate) {
      return
    }

    setAppliedStartDate(startDate)
    setAppliedEndDate(endDate)

    const updated = new URLSearchParams(searchParams)
    updated.set('period', 'custom')
    updated.set('startDate', startDate)
    updated.set('endDate', endDate)
    setSearchParams(updated)
  }

  /*
   * View quotation
   */
  const handleViewQuote = async (quote) => {
    try {
      const quotation = await getQuotationById(quote._id)

      setSelectedQuote(quotation)
      setIsDetailsOpen(true)
    } catch (error) {
      console.error('Failed to fetch quotation:', error)
    }
  }

  /*
   * Open send quotation modal
   */
  const handleSendQuote = async (quote) => {
    try {
      const quotation = await getQuotationById(quote._id)

      setSelectedQuote(quotation)
      setIsSendOpen(true)
    } catch (error) {
      console.error('Failed to fetch quotation:', error)
    }
  }

  /*
   * Edit quotation — navigate to the full edit page
   */
  const handleEditQuote = (quote) => {
    navigate(`/sales/quotes/edit/${quote._id}`)
  }

  /*
   * Delete quotation
   */
  const handleDeleteQuote = (quote) => {
    setDeleteQuote(quote)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      await deleteQuotation(deleteQuote._id)
      toast.success('Quotation deleted successfully')
      setIsDeleteOpen(false)
      setDeleteQuote(null)
      await getQuotations(quotationParams)
    } catch (error) {
      toast.error(error?.message || 'Failed to delete quotation')
    } finally {
      setIsDeleting(false)
    }
  }

  /*
   * Export currently filtered quotations
   *
   * The quotations array already contains the data
   * filtered by:
   * - Period
   * - Custom date range
   * - Search
   */
  const handleExport = () => {
    const rows = []

    quotations.forEach((quote) => {
      const products =
        Array.isArray(quote.products) && quote.products.length > 0
          ? quote.products
          : [{ product: '-', quantity: 0 }]

      products.forEach((product, index) => {
        rows.push({
          'Quote No': index === 0 ? (quote.quoteNo ?? '') : '',

          'Contact Name': index === 0 ? (quote.contactName ?? '') : '',

          'Account Name': index === 0 ? (quote.accountName ?? '') : '',

          Product: product.product || '-',

          Quantity: product.quantity ?? 0,

          Status: index === 0 ? quote.status || '' : '',

          'Valid Until': index === 0 ? quote.validUntil || '-' : '',
        })
      })
    })

    if (!rows.length) {
      return
    }

    downloadCsv('sales-quotations.csv', rowsToCsv(rows))
  }

  /*
   * Refresh quotations after sending
   */
  const handleQuotationSent = async () => {
    await getQuotations(quotationParams)

    getQuotationStats(quotationParams)
      .then((response) => {
        setQuotationStats(response?.data || null)
      })
      .catch(() => {
        setQuotationStats(null)
      })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <QuoteHeader onExport={handleExport} />

      {/* Summary Cards */}
      <QuoteSummaryCards quotations={quotations} stats={quotationStats} />

      {/* Quotations Table */}
      <div className="rounded-3xl border bg-white p-6 shadow-sm">
        <DataTable
          columns={QuoteColumns(
            handleViewQuote,
            handleSendQuote,
            false,
            handleEditQuote,
            handleDeleteQuote,
          )}
          data={quotations}
          loading={loading}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: 'Search quote # or customer...',
          }}
          toolbarExtra={
            <QuoteFilters
              total={quotations.length}
              filtered={quotations.length}
              period={period}
              onPeriodChange={handlePeriodChange}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onApplyCustomDate={handleApplyCustomDate}
            />
          }
          mobileCard={(row) => (
            <QuoteMobileCard
              quote={row}
              onView={handleViewQuote}
              onSend={handleSendQuote}
            />
          )}
          pagination={{
            page: meta?.page ?? page,
            pageSize: meta?.limit ?? 5,
            total: meta?.total ?? 0,
            onPageChange: (newPage) => setPage(newPage),
          }}
        />

        {/* Quotation Details */}
        <QuoteDetailsModal
          open={isDetailsOpen}
          onOpenChange={setIsDetailsOpen}
          quote={selectedQuote}
        />

        {/* Send Quotation */}
        <SendQuotationModal
          open={isSendOpen}
          onOpenChange={setIsSendOpen}
          quotation={selectedQuote}
          onSuccess={handleQuotationSent}
        />

        {/* Delete Confirm */}
        <QuoteDeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          quote={deleteQuote}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      </div>
    </div>
  )
}
